import React from "react";
import { COMPANY } from "@/data/company";
import PLUMBER_SCHEMA_JSON from "@/data/plumber-schema.json";
import { SERVICES } from "@/data/services";
import { BLOG_AUTHOR, postWordCount } from "@/data/blog";

// JSON-LD block rendered where the component sits (React 19 does not hoist
// non-async scripts, and inline <script> elements React inserts never execute,
// which is exactly right for data blocks). "<" is escaped so page data can
// never close the script tag early; the result is still valid JSON.
const jsonLd = (data) => JSON.stringify(data).replace(/</g, "\\u003c");
export const JsonLd = ({ data }) => (
  <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd(data) }} />
);

// Central SEO component: unique titles, descriptions, canonical, OG, JSON-LD.
// `noindex` marks internal / error pages (admin, 404) so they never appear in
// search results or Search Console coverage reports — they get a robots meta
// and NO canonical / og:url.
//
// The tags are plain React 19 elements: React hoists <title>, <meta> and <link>
// into document.head in the browser (and removes them when the page unmounts),
// so every page has exactly one set of head tags, in <head>. At build time the
// prerenderer (scripts/prerender-entry.jsx) collects the same hoisted tags from
// the server render and writes them into <head> of the static HTML, so crawlers
// that do not run JavaScript see identical values. react-helmet-async is no
// longer used: on React 19 it renders these tags inline and leaves its SSR
// context empty, which is what put them inside #root before.
export const SEO = ({ title, description, path = "", image, schema, noindex = false }) => {
  const url = `${COMPANY.siteUrl}${path}`;
  // Root-relative image paths (e.g. /images/brand/x.jpg) are absolutized for
  // social crawlers, which require fully-qualified og:image URLs.
  const ogImage = image && image.startsWith("/") ? `${COMPANY.siteUrl}${image}` : image;
  const schemas = schema ? (Array.isArray(schema) ? schema : [schema]) : [];
  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      {noindex ? <meta name="robots" content="noindex, nofollow" /> : <link rel="canonical" href={url} />}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={COMPANY.name} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {!noindex && <meta property="og:url" content={url} />}
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {schemas.map((s, i) => (
        <JsonLd key={`${s["@type"] || "schema"}-${i}`} data={s} />
      ))}
    </>
  );
};

// ---- JSON-LD builders ----
//
// Google's LocalBusiness rich-result rules require every LocalBusiness/Plumber
// entity to carry name, address, telephone, url and image. The business itself
// is defined exactly once per page (PLUMBER_SCHEMA, rendered by Layout.jsx on
// every page); every other block refers to it by @id.

const absoluteUrl = (src) => (src && src.startsWith("/") ? `${COMPANY.siteUrl}${src}` : src);

// The ONE business node (SEO audit batch 1, Part 4A): the owner-approved
// Plumber block in src/data/plumber-schema.json, verbatim. Every page that
// mentions the company - homepage, hubs, service `provider`, city `provider`,
// blog `publisher` - points at this @id so Google merges them into a single
// LocalBusiness instead of reading 41 city pages as 41 separate "businesses"
// at the same address (the doorway-page fingerprint). No aggregateRating /
// review properties until there are real reviews.
export const PLUMBER_SCHEMA = PLUMBER_SCHEMA_JSON;
export const PLUMBER_ID = PLUMBER_SCHEMA["@id"];
const plumberRef = () => ({ "@id": PLUMBER_ID });

/**
 * Offer catalog listing every service page. Attached to city / service-area
 * Service nodes so each local page says "this business offers these
 * services here" instead of declaring a separate business.
 */
const offerCatalog = (placeName) => ({
  "@type": "OfferCatalog",
  name: `Plumbing services in ${placeName}`,
  itemListElement: SERVICES.map((s) => ({
    "@type": "Offer",
    url: `${COMPANY.siteUrl}/services/${s.slug}`,
    itemOffered: {
      "@type": "Service",
      name: s.name,
      url: `${COMPANY.siteUrl}/services/${s.slug}`,
      provider: plumberRef(),
    },
  })),
});

/**
 * Local-market Service node: "Plumbing services in <Place>, WA", provided by
 * the single #plumber business. Used for the 41 city pages and the Vancouver /
 * Spokane service-area pages (no walk-in office there).
 *
 * @param {object} p
 * @param {string} p.path         page path, e.g. "/plumber/renton"
 * @param {string} p.name         city / area name, e.g. "Renton"
 * @param {string} p.county       e.g. "King County"
 * @param {string} p.description  the page's meta description (unchanged)
 * @param {string[]} p.places     neighborhoods (City page) or communities (service area)
 * @param {"Place"|"City"} p.placeType  schema type for `places`
 * @param {string} [p.image]
 */
export const buildLocalServiceSchema = ({ path, name, county, description, places = [], placeType = "Place", image }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${COMPANY.siteUrl}${path}#service`,
  serviceType: "Plumbing services",
  name: `Plumbing services in ${name}, WA`,
  url: `${COMPANY.siteUrl}${path}`,
  description,
  ...(image ? { image: absoluteUrl(image) } : {}),
  provider: plumberRef(),
  areaServed: [
    {
      "@type": "City",
      name,
      containedInPlace: { "@type": "AdministrativeArea", name: `${county}, WA` },
    },
    ...places.filter((p) => p !== name).map((p) => ({ "@type": placeType, name: p })),
  ],
  hasOfferCatalog: offerCatalog(name),
});

/**
 * Location hub pages. Federal Way is the real office (the single #plumber
 * business node, present on every page). Vancouver / Spokane are mobile-crew
 * service areas and get the Service treatment.
 */
export const buildLocationSchema = (office, location) => {
  const trail = [{ name: "Locations", path: "/locations" }, { name: `${location.city}, WA`, path: `/locations/${office.slug}` }];
  if (!office.isServiceArea) {
    // The HQ page: the business node itself is already on the page (Layout
    // renders PLUMBER_SCHEMA everywhere), so only the breadcrumb is added here.
    return [buildBreadcrumbSchema(trail)];
  }
  return [
    buildLocalServiceSchema({
      path: `/locations/${office.slug}`,
      name: location.city,
      county: location.county,
      description: location.metaDescription,
      places: location.areas,
      placeType: "City",
      image: location.image,
    }),
    buildBreadcrumbSchema(trail),
  ];
};

/**
 * BreadcrumbList for any page. `trail` is [{ name, path }] from Home down to
 * the current page; Home is added automatically.
 */
export const buildBreadcrumbSchema = (trail) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [{ name: "Home", path: "" }, ...trail].map((crumb, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: crumb.name,
    item: `${COMPANY.siteUrl}${crumb.path}`,
  })),
});

export const buildServiceSchema = (service) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: service.name,
  name: service.name,
  description: service.excerpt,
  url: `${COMPANY.siteUrl}/services/${service.slug}`,
  image: absoluteUrl(service.image),
  provider: plumberRef(),
  areaServed: { "@type": "State", name: "Washington" },
});

export const buildFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

// ---- Blog ----
//
// One Blog node on /blog (#blog) lists every post as a lightweight BlogPosting
// reference; each post page carries the full BlogPosting under the same @id
// (<post url>#post) and points back with isPartOf. Author stays the
// Organization (owner decision - no invented Person). `publisher` is a
// reference to the single #plumber business defined on the homepage, not a
// second inline Organization (Prompt 02 rule: one business entity sitewide).

export const BLOG_ID = `${COMPANY.siteUrl}/blog#blog`;
export const BLOG_NAME = "IDB Plumbing Blog & Resources";
const blogPostId = (post) => `${COMPANY.siteUrl}/blog/${post.slug}#post`;

export const buildBlogPostingSchema = (post) => {
  const url = `${COMPANY.siteUrl}/blog/${post.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": blogPostId(post),
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    url,
    headline: post.title,
    description: post.metaDescription || post.excerpt,
    image: absoluteUrl(post.image),
    articleSection: post.category,
    wordCount: postWordCount(post),
    inLanguage: "en-US",
    datePublished: post.date,
    dateModified: post.updated || post.date,
    // Batch 3, Part 6: a named Person (no invented bio), employed by the business entity.
    author: {
      "@type": "Person",
      name: BLOG_AUTHOR.name,
      jobTitle: "Co-owner",
      worksFor: plumberRef(),
      url: `${COMPANY.siteUrl}/about`,
    },
    publisher: plumberRef(),
    isPartOf: { "@type": "Blog", "@id": BLOG_ID, name: BLOG_NAME },
  };
};

/**
 * /blog index: a Blog node whose blogPost[] references every published post
 * (newest first, same order as the cards). Each entry carries only identity
 * fields; the full BlogPosting lives on the post page under the same @id.
 */
export const buildBlogIndexSchema = (posts, description) => ({
  "@context": "https://schema.org",
  "@type": "Blog",
  "@id": BLOG_ID,
  name: BLOG_NAME,
  url: `${COMPANY.siteUrl}/blog`,
  ...(description ? { description } : {}),
  inLanguage: "en-US",
  publisher: plumberRef(),
  blogPost: posts.map((post) => ({
    "@type": "BlogPosting",
    "@id": blogPostId(post),
    headline: post.title,
    url: `${COMPANY.siteUrl}/blog/${post.slug}`,
    datePublished: post.date,
    dateModified: post.updated || post.date,
  })),
});


// City landing page: a Service node ("Plumbing services in <City>, WA")
// provided by the single #plumber business, with the city + its neighborhoods
// as areaServed and every service page in an OfferCatalog. Previously each
// city page declared its own Plumber entity at the HQ address - 41 near-
// identical businesses, which is exactly what Google's doorway-page
// classifier looks for.
export const buildCitySchema = (city) => [
  buildLocalServiceSchema({
    path: `/plumber/${city.slug}`,
    name: city.name,
    county: city.county,
    description: city.metaDescription,
    places: city.neighborhoods || [],
    placeType: "Place",
    image: city.image,
  }),
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: COMPANY.siteUrl },
      { "@type": "ListItem", position: 2, name: "Locations", item: `${COMPANY.siteUrl}/locations` },
      { "@type": "ListItem", position: 3, name: `${city.name}, WA`, item: `${COMPANY.siteUrl}/plumber/${city.slug}` },
    ],
  },
];
